<template>
  <div>
    <div class="text-center mb-5">
      <h1>Bienvenue</h1>
      <h2 class="lead h4 text-primary">Saisissez votre mot de passe</h2>
    </div>
    <b-form @submit.stop.prevent="onSubmit">
      <b-form-group
        id="password"
        label="Mot de passe"
        label-for="password-input"
      >
        <b-form-input
          type="password"
          id="password-input"
          name="password-input"
          @change="setValue('password', $event)"
          :value="form.password"
          :state="validateState('password')"
          aria-describedby="input-password-live-feedback"
        ></b-form-input>

        <b-form-invalid-feedback id="input-password-live-feedback">
          Le mot de passe doit contenir au moins 8 caractères
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group
        id="repeatPassword"
        label="Confirmation du mot de passe"
        label-for="repeatPassword-input"
      >
        <b-form-input
          type="password"
          id="repeatPassword-input"
          name="repeatPassword-input"
          @change="setValue('repeatPassword', $event)"
          :value="form.repeatPassword"
          :state="validateState('repeatPassword')"
          aria-describedby="input-repeatPassword-live-feedback"
        ></b-form-input>

        <b-form-invalid-feedback id="input-repeatPassword-live-feedback">
          Les mots de passes doivent être identiques
        </b-form-invalid-feedback>
      </b-form-group>

      <div class="d-flex justify-content-end align-items-center">
        <b-btn
          pill
          :disabled="authStatus == 'fetching'"
          class="px-4"
          type="submit"
          variant="primary"
        >
          <span>Sauvegarder</span>
          <b-spinner
            class="ml-2"
            small
            v-if="authStatus == 'fetching'"
          ></b-spinner>
        </b-btn>
      </div>
    </b-form>
  </div>
</template>

<script>
/**
 * @file       Set password component.
 * @author     Appwapp <info@appwapp.com>
 * @copyright  © Centre collégial de développement de matériel didactique, Collège de Maisonneuve, 2021
 * @license    https://www.ccdmd.qc.ca
 * @version    1.0
 */
import { required, minLength, sameAs } from "vuelidate/lib/validators";
import { mapState } from "vuex";

/**
 * Export component.
 * 
 * @type  {Object}
 */
export default {
  /**
   * Data used in the component.
   *
   * @return  {Object}
   */
  data() {
    return {
      form: {
        password: null,
        repeatPassword: null
      }
    };
  },

  /**
   * Vuelidate form validations.
   * 
   * @type  {Object}
   */
  validations: {
    form: {
      password: {
        required,
        minLength: minLength(8)
      },
      repeatPassword: {
        sameAsPassword: sameAs("password")
      }
    }
  },

  /**
   * Computed properties.
   * 
   * @type  {Object}
   */
  computed: {
    ...mapState({
      authStatus: state => state.auth.password.status
    })
  },

  /**
   * Methods.
   * 
   * @type  {Object}
   */
  methods: {
    /**
     * Sets the form value.
     *
     * @param      {string}  key     The new value
     * @param      {string}  value   The value
     */
    setValue(key, value) {
      this.form[key] = value;
      this.$v.form[key].$touch();
    },

    /**
     * Validate the form state.
     *
     * @param      {string}  name    The input name
     *
     * @return     {bool}  Dirty or not.
     */
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name];
      return $dirty ? !$error : null;
    },

    /**
     * Resets the form.
     */
    resetForm() {
      this.form = {
        password: null,
        repeatPassword: null
      };

      // Resets on the next tick
      this.$nextTick(() => {
        this.$v.$reset();
      });
    },

    /**
     * Called on submit.
     */
    onSubmit() {
      this.$v.form.$touch();

      // If any error found, stop the submit
      if (this.$v.form.$anyError) {
        return;
      }

      // Remove repeatPassword from the data
      delete this.form.repeatPassword;

      // Dispatch the set password request
      this.$store
        .dispatch("auth/setPassword", {
          ...this.$route.params,
          ...this.form
        })
        .then(() => {
          // Display a success message
          this.$root.$bvToast.toast("Votre mot de passe a été sauvegardé", {
            title: "Set password success"
          });

          // Redirects to login options
          this.$router.push({ name: "auth.login.options" });
        })
        .catch(() => {
          // Display an error message
          this.$root.$bvToast.toast(
            "Votre mot de passe n'a pu être sauvegardé",
            {
              title: "Erreur",
              variant: "danger"
            }
          );
        });
    }
  }
};
</script>
